/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as MultiIndicatorRingGaugeLayerSpec } from '../../../../layer/preset/gauge/multiIndicatorRingGauge';
import { checkFieldType } from '../helper';
import * as utils from '../../../../util';
import { BaseSpec, ILayerTypeSpec, createLayerProcessor } from '../baseProcessor';

export type Spec = BaseSpec;

type MultiIndicatorRingGaugeLayerType = StandardChartLayerSpec &
  MultiIndicatorRingGaugeLayerSpec &
  ILayerTypeSpec;

// 由于x字段是数组类型，我们不能使用标准的createDataTypeValidator函数
// 创建自定义数据类型验证函数
const validateMultiIndicatorRingGaugeDataTypes = (
  layer: MultiIndicatorRingGaugeLayerType,
  data: Data
): boolean => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return false;
  }

  // 根据文档要求进行数据类型检测
  let isValid = true;

  // 检查X轴字段类型 - 必须是数值类型
  if (Array.isArray(encoding.x)) {
    encoding.x.forEach(xField => {
      if (!checkFieldType(values, xField, 'number')) {
        utils.logger.warn(
          'warning',
          `The "x" field "${xField}" of the "${layer.type}" layer must be a number`
        );
        isValid = false;
      }
    });
  } else {
    utils.logger.warn(
      'warning',
      `The "x" field of the "${layer.type}" layer must be an array of fields`
    );
    isValid = false;
  }

  return isValid;
};

// 创建自定义编码验证函数
const validateMultiIndicatorRingGaugeEncoding = (
  layer: MultiIndicatorRingGaugeLayerType
): boolean => {
  const { encoding } = layer;

  // 检查x字段是否为数组
  if (!Array.isArray(encoding.x)) {
    utils.logger.warn(
      'warning',
      `The "x" field of the "${layer.type}" layer must be an array of fields`
    );
    return false;
  }

  return true;
};

// 额外的检查函数
const additionalMultiIndicatorRingGaugeChecks = (
  layer: MultiIndicatorRingGaugeLayerType,
  data: Data
) => {
  // 检查数据量是否符合要求
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];
  if (values.length !== 1) {
    utils.logger.warn(
      'warning',
      `The "${layer.type}" layer requires exactly 1 data point, but got ${values.length}`
    );
  }
};

// 创建并导出多指标环状仪表盘处理器
export const multiIndicatorRingGauge = createLayerProcessor<MultiIndicatorRingGaugeLayerType>(
  'multiIndicatorRingGauge',
  validateMultiIndicatorRingGaugeEncoding,
  validateMultiIndicatorRingGaugeDataTypes,
  additionalMultiIndicatorRingGaugeChecks
);
